import { Inject, Injectable, Optional } from '@angular/core';
import { Platform } from '@angular/cdk/platform';
import { NativeDateTimeAdapter, OWL_DATE_TIME_FORMATS, OWL_DATE_TIME_LOCALE, OwlDateTimeFormats } from '@danielmoncada/angular-datetime-picker';
import moment from 'moment';
import { ReportDateService } from './report-date.service';

/**
 * Owl's native adapter, but a typed date is read and shown in the studio's
 * format (MM/DD/YYYY, DD/MM/YYYY or YYYY-MM-DD). The stock adapter uses
 * Date.parse, which always assumes month-first and reads ISO dates as UTC
 * (the day before, in US timezones). The calendar itself is unchanged.
 *
 * Provide it on a component with a typeable owl date input:
 *   providers: [{ provide: DateTimeAdapter, useClass: StudioDateTimeAdapter }]
 */
@Injectable()
export class StudioDateTimeAdapter extends NativeDateTimeAdapter {

  constructor(
    @Optional() @Inject(OWL_DATE_TIME_LOCALE) owlDateTimeLocale: string,
    platform: Platform,
    @Inject(OWL_DATE_TIME_FORMATS) private owlFormats: OwlDateTimeFormats,
    private reportDateService: ReportDateService,
  ) {
    super(owlDateTimeLocale, platform);
  }

  override parse(value: any, parseFormat: any): Date | null {
    if (typeof value !== 'string') {
      return super.parse(value, parseFormat);
    }

    const text = value.trim();
    if (!text) {
      return null;
    }

    // Accept the studio's field order with any separator, single-digit day and
    // month, no separators at all, or ISO.
    const studioFormat = this.reportDateService.calendarDateFormat();
    const loose = studioFormat.replace('MM', 'M').replace('DD', 'D');
    const separated = ['/', '-', '.'].map(separator => loose.replace(/[\/\-.]/g, separator));
    const fullYear = [...separated, studioFormat.replace(/[\/\-.]/g, ''), 'YYYY-M-D'];

    let parsed = moment(text, fullYear, true);

    // Two-digit years resolve to the most recent past century (5/12/45 is
    // 1945, not 2045).
    if (!parsed.isValid()) {
      const shortYear = fullYear.map(format => format.replace('YYYY', 'YY'));
      parsed = moment(text, shortYear, true);
      if (parsed.isValid() && parsed.isAfter(moment(), 'day')) {
        parsed.subtract(100, 'years');
      }
    }

    // An Invalid Date (not null) makes owl flag the input as unparseable.
    return parsed.isValid() ? parsed.toDate() : new Date(NaN);
  }

  override format(date: Date, displayFormat: any): string {
    if (displayFormat === this.owlFormats?.datePickerInput && this.isValid(date)) {
      return moment(date).format(this.reportDateService.calendarDateFormat());
    }
    return super.format(date, displayFormat);
  }
}
